<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import locationTypeService from '@/services/locationType.service'
import LocationTypeDrawer from '@/views/masters/locationType/drawer/LocationTypeDrawer.vue'
import { VDataTableServer } from 'vuetify/labs/VDataTable'

definePage({
  meta: {
    name: 'master-location-type',
    subject: 'global.menu',
    action: 'master_location_type',
  },
})

const selectedUnit = cookieRef('selectedUnit')

const searchQuery = ref('')
const debouncedSearch = refDebounced(searchQuery, 500)
const isConfirmDialogVisible = ref(false)

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const errors = ref({
  name: [],
  code: [],
  status: [],
})

const onError = error => {
  if (error.data) {
    errors.value = error.data.errors
  }
}

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

// Headers
const headers = [
  { title: 'Name', key: 'name' },
  { title: 'Order', key: 'order' },
  { title: 'Unit', key: 'unit.name', sortable: false },
  { title: 'Actions', key: 'actions', sortable: false },
]

const {
  data: locationTypesData,
  isFetching: isFetchingLocationTypes,
  execute: fetchLocationTypes,
} = await useApi(createUrl('/masters/location-types', {
  query: {
    paged: 1,
    q: debouncedSearch,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
    search: {
      unit_id: selectedUnit,
    },
  },
}))

const locationTypes = computed(() => locationTypesData.value?.data ?? [])
const totalCategories = computed(() => locationTypesData.value?.meta?.total ?? 0)

const updateLocationType = data => {
  if (data.id) {
    locationTypeService.updateLocationType(data.id, data, () => {
      fetchLocationTypes()
      isLocationTypeDrawerVisible.value = false
      resetErrors()
    }, onError)
  }
}

const saveLocationType = data => {
  locationTypeService.addLocationType(data, () => {
    fetchLocationTypes()
    isLocationTypeDrawerVisible.value = false
    resetErrors()
  }, onError)
}

const resetErrors = () => {
  errors.value = {
    name: [],
    code: [],
    status: [],
  }
}

const isLocationTypeDrawerVisible = ref(false)
const locationTypeDrawerData = ref()
const locationTypeDeleteData = ref()
const locationTypeDrawerType = ref('Add')

const showLocationType = (locationTypeData, type) => {
  locationTypeDrawerData.value = locationTypeData
  locationTypeDrawerType.value = type
  isLocationTypeDrawerVisible.value = true
}

const deleteLocationType = async (confirm, data) => {
  if (data?.id && confirm) {
    await locationTypeService.deleteLocationType(data.id, () => {
      fetchLocationTypes()
      isConfirmDialogVisible.value = false
    }, onError)
  }
}

const showConfirmDialog = locationTypeData => {
  locationTypeDeleteData.value = locationTypeData
  isConfirmDialogVisible.value = true
}

watch(isConfirmDialogVisible, () => {
  if (!isConfirmDialogVisible.value) {
    locationTypeDeleteData.value = {}
  }
})
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border"
    >
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div style="inline-size: 10rem;">
          <AppTextField
            v-model="searchQuery"
            placeholder="Search"
            density="compact"
          />
        </div>

        <VSpacer />

        <VBtn
          v-if="$can('create', 'master.location_type')"
          variant="flat"
          prepend-icon="tabler-plus"
          @click="showLocationType({}, 'Add')"
        >
          Add New Location Type
        </VBtn>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="locationTypes"
        :items-length="totalCategories"
        :headers="headers"
        :loading="isFetchingLocationTypes"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.name="{ item }">
          <span
            class="cursor-pointer text-base font-weight-medium"
            @click="showLocationType(item, 'View')"
          >
            {{ item.name }}
          </span>
        </template>

        <template #item.unit.name="{ item }">
          <span class="text-medium-emphasis">{{ item.unit?.name ?? '—' }}</span>
        </template>

        <template #item.actions="{ item }">
          <IconBtn
            v-if="$can('delete', 'master.location_type')"
            @click="showConfirmDialog(item)"
          >
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <IconBtn
            v-if="$can('update', 'master.location_type')"
            @click="showLocationType(item, 'Edit')"
          >
            <VIcon icon="tabler-edit" />
          </IconBtn>
        </template>
      </VDataTableServer>
    </VCard>

    <LocationTypeDrawer
      v-model:isDrawerOpen="isLocationTypeDrawerVisible"
      :location-type-data="locationTypeDrawerData"
      :type="locationTypeDrawerType ?? 'Add'"
      :errors="errors"
      @update:type="locationTypeDrawerType = $event"
      @update:location-type-data="updateLocationType"
      @save-location-type="saveLocationType"
    />

    <ConfirmDialog
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="LocationType Deleted."
      confirm-title="Deleted!"
      :data="locationTypeDeleteData"
      @confirm="deleteLocationType"
    />
  </section>
</template>

<style lang="css">
.v-data-table-footer {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}
</style>
